// deps
import React, { memo, ReactElement, useCallback, useContext, useMemo, useRef, useState } from "react";

// internal-deps
import {
    RouteProps,
    useEffectOnce,
    GenericError,
    ApplyCouponInputBox,
    CouponCardGroup,
    ApplyingCouponBottomSheet,
    CouponListingShimmer,
    ICouponCard,
    isCurrentSystemThemeDark,
} from "@swiggy-private/core-ui";
import {
    AnalyticsContext,
    recordGenericErrorNREvent,
    DineoutCartContext,
    IDineoutLocalCart as ILocalCart,
} from "@swiggy-private/checkout-core";

// components
import { CouponUIWrapper } from "./coupon-ui-wrapper";

import {
    CART_GENERIC_EVENT_CASES,
    EMPTY_COUPON_PAGE_SUBTITLE,
    EMPTY_COUPON_PAGE_TITLE,
} from "@dineout-cart-client/constants/Coupon";
import { isEmpty } from "@swiggy-private/js-utils";

import {
    clickCouponOffersEvent,
    clickMoreDetailsEvent,
    impressionOffersEvent,
    impressionCouponOffersEvent,
    impressionPaymentBannerEvent,
    impressionRewardOffersEvent,
} from "../../events/CouponListing";
import { getPaymentOfferCoupons, isPaymentOfferCouponGroup } from "@dineout-cart-client/helpers/CouponListing";

import { CouponCardImpressionPayload } from "@swiggy-private/core-types";
import { DineoutCheckoutRoute } from "@swiggy-private/checkout-types";
import { CouponListingContext } from "@dineout-cart-client/contexts/CouponListingContext";
import { EMPTY_TICKET_STATE_TEXT, RETRY } from "@dineout-cart-client/constants";
import { UpdateCartContext } from "@dineout-cart-client/contexts/index";

import { getDealCategory } from "@dineout-cart-client/utils/index";
import { CartMetaData } from "@dineout-cart-client/interfaces/dineoutCartTypes";

type CouponListingProps = RouteProps<DineoutCheckoutRoute>;

export const CouponListing = memo(function CouponListing(_props: CouponListingProps): ReactElement | null {
    const { cartState } = useContext(DineoutCartContext);
    const { localCart, reviewedCart } = cartState;
    const cartResponse = useMemo(() => reviewedCart?.data, [reviewedCart]);

    const dealCategory = getDealCategory(cartResponse);

    const cartTotal = useMemo(() => {
        return cartResponse?.bill?.dineoutPricingInfo?.billAmountToApplyCoupon || 0;
    }, [cartResponse]);

    const restaurantId = useMemo(() => {
        const vendorId = cartResponse?.storesInfo?.[0]?.vendorIdForApplyCoupon;
        return vendorId ?? localCart?.cartMetaData?.restaurantId ?? "";
    }, [localCart?.cartMetaData?.restaurantId, cartResponse?.storesInfo]);

    // This is only applicable for scenes
    const eventId = useMemo(() => {
        return cartResponse?.aggregatedItemsInfo?.eventId;
    }, [cartResponse?.aggregatedItemsInfo?.eventId]);

    const isPartPaySelected = useMemo(() => {
        return localCart?.cartMetaData?.isPartialPaymentApplied;
    }, [localCart?.cartMetaData?.isPartialPaymentApplied]);

    const tableId = localCart?.cartMetaData?.tableId;

    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    const [couponCode, setCouponCode] = useState<string>("");
    const { syncApplyCouponCart } = useContext(UpdateCartContext);

    const {
        fetchCoupons,
        isFetching,
        isApplyingCoupon,
        isRemovingCoupon,
        errorMessage,
        clearErrorMessage,
        couponsPayload,
        nextBestCoupon,
        resetCouponListingPage,
        hasInitiallyFetched,
    } = useContext(CouponListingContext);
    const paymentOfferCoupons = useMemo(() => getPaymentOfferCoupons(couponsPayload.data), [couponsPayload]);
    const isPaymentOffer = isPaymentOfferCouponGroup(paymentOfferCoupons?.type);
    const paymentOffersRef = useRef<HTMLDivElement>(null);

    const applyCouponHandler = useCallback(
        (payload: CouponCardImpressionPayload) => {
            const { offerId, isApplicable, couponCode } = payload;
            if (offerId && isApplicable) {
                clickCouponOffersEvent(clickEvent, couponCode || "", {
                    storeId: restaurantId,
                    offerId,
                    isApplicable: String(isApplicable),
                });
            }
            const updatedLocalCart: ILocalCart = {
                ...localCart,

                cartMetaData: {
                    ...localCart?.cartMetaData,
                    couponCode: couponCode,
                } as CartMetaData,
            };
            syncApplyCouponCart(updatedLocalCart);
        },
        [clickEvent, restaurantId, localCart, syncApplyCouponCart],
    );

    const closeBottomSheet = useCallback(() => {
        clearErrorMessage();
    }, [clearErrorMessage]);

    const reloadCouponListing = useCallback(() => {
        setCouponCode("");
        resetCouponListingPage();
        fetchCoupons({ restaurantId, cartValue: cartTotal, dealCategory, entityId: eventId, isPartPaySelected, tableId });
    }, [cartTotal, dealCategory, eventId, fetchCoupons, resetCouponListingPage, restaurantId, isPartPaySelected, tableId]);

    const clickMoreDetailsEventHandler = useCallback(
        (couponCode: string, couponType: string) => {
            clickMoreDetailsEvent(clickEvent, couponCode, couponType);
        },
        [clickEvent],
    );

    const showErrorScreen = useMemo(() => {
        return !isFetching && !isEmpty(couponsPayload?.error) && isEmpty(couponsPayload.data);
    }, [couponsPayload.data, couponsPayload?.error, isFetching]);

    useEffectOnce(() => {
        impressionOffersEvent(impressionEvent);
        fetchCoupons({ restaurantId, cartValue: cartTotal, dealCategory, entityId: eventId, isPartPaySelected, tableId });
    });

    const showBottomSheet = Boolean(isApplyingCoupon || errorMessage.trim() || isRemovingCoupon);

    const couponOffersImpressionEvent = useCallback(
        (payload: CouponCardImpressionPayload) => {
            const { offerId, isApplicable, couponCode } = payload;
            impressionCouponOffersEvent(impressionEvent, {
                offerId: offerId || "",
                isApplicable: isApplicable || false,
                couponCode: couponCode || "",
            });
        },
        [impressionEvent],
    );

    const rewardOfferImpressionEvent = useCallback(
        (payload: CouponCardImpressionPayload) => {
            const { couponCode } = payload;
            impressionRewardOffersEvent(impressionEvent, couponCode || "");
        },
        [impressionEvent],
    );

    const paymentBannerImpressionEvent = useCallback(
        (bannerText: string) => {
            impressionPaymentBannerEvent(impressionEvent, bannerText);
        },
        [impressionEvent],
    );

    const isCouponListingEmpty = useMemo(() => {
        return !isFetching && hasInitiallyFetched && isEmpty(couponsPayload.data);
    }, [couponsPayload.data, hasInitiallyFetched, isFetching]);

    const onImpressionErrorPage = useCallback(
        () =>
            recordGenericErrorNREvent({
                errorContext: CART_GENERIC_EVENT_CASES.EMPTY_COUPON_LISTING_PAGE,
            }),
        [],
    );

    const onRetryClick = useCallback(() => {
        fetchCoupons({
            restaurantId,
            cartValue: cartTotal,
            dealCategory,
            entityId: eventId,
            isPartPaySelected,
            tableId,
        });
    }, [fetchCoupons, restaurantId, cartTotal, dealCategory, eventId, isPartPaySelected, tableId]);

    if (showErrorScreen) {
        return (
            <CouponUIWrapper cartTotal={cartTotal.toString()} isLoading={isFetching}>
                <GenericError
                    action={{
                        text: RETRY,
                        onClick: onRetryClick,
                    }}
                ></GenericError>
            </CouponUIWrapper>
        );
    }

    if (isCouponListingEmpty) {
        return (
            <CouponUIWrapper cartTotal={cartTotal.toString()} isLoading={isFetching}>
                <GenericError
                    pageImpressionEvent={onImpressionErrorPage}
                    title={EMPTY_COUPON_PAGE_TITLE}
                    message={EMPTY_COUPON_PAGE_SUBTITLE}
                    primaryAction={null}
                    action={null}
                    imageId={
                        isCurrentSystemThemeDark()
                            ? EMPTY_TICKET_STATE_TEXT.IMAGE_URL.dark
                            : EMPTY_TICKET_STATE_TEXT.IMAGE_URL.light
                    }
                    contentClassName=""
                ></GenericError>
            </CouponUIWrapper>
        );
    }

    return (
        <CouponUIWrapper cartTotal={cartTotal.toString()} isLoading={isFetching}>
            <ApplyCouponInputBox
                isLoading={isFetching}
                ref={isPaymentOffer ? paymentOffersRef : null}
                onApplyClick={applyCouponHandler}
                couponCode={couponCode}
                setCouponCode={setCouponCode}
            />

            {isFetching ? (
                <CouponListingShimmer />
            ) : (
                couponsPayload.data?.map((card: ICouponCard) => (
                    <CouponCardGroup
                        key={card.type}
                        couponGroup={card}
                        onApplyCouponClick={applyCouponHandler}
                        clickMoreDetailsEvent={clickMoreDetailsEventHandler}
                        couponOffersImpressionEvent={couponOffersImpressionEvent}
                        rewardOfferImpressionEvent={rewardOfferImpressionEvent}
                        paymentBannerImpressionEvent={paymentBannerImpressionEvent}
                    />
                ))
            )}

            {showBottomSheet && (
                <ApplyingCouponBottomSheet
                    loading={isApplyingCoupon}
                    closeBottomSheet={closeBottomSheet}
                    reloadCouponListing={reloadCouponListing}
                    errorMessage={errorMessage}
                    nextBestCoupon={nextBestCoupon}
                    onApplyCouponClick={applyCouponHandler}
                    clickMoreDetailsEvent={clickMoreDetailsEventHandler}
                />
            )}
        </CouponUIWrapper>
    );
});
